import { useEffect, useRef, useState } from "react";

/**
 * Animate a numeric value from 0 -> target whenever the element enters viewport.
 * Returns [ref, displayValue]. The displayValue is a number; format outside.
 */
export function useCountUp(target, { duration = 1400, decimals = 0, start = 0 } = {}) {
  const ref = useRef(null);
  const [value, setValue] = useState(start);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const t0 = performance.now();
            const from = start;
            const tick = (now) => {
              const p = Math.min(1, (now - t0) / duration);
              const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
              const v = from + (target - from) * eased;
              setValue(decimals ? +v.toFixed(decimals) : Math.round(v));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            obs.disconnect();
          }
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration, decimals, start]);

  return [ref, value];
}
